import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Rational function parameters: a / (x - h) + k */
export interface RationalParams {
  /** Vertical scale */
  a: number;
  /** Horizontal shift (vertical asymptote at x = h) */
  h: number;
  /** Vertical shift (horizontal asymptote at y = k) */
  k: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for rational functions.
 * Handle is placed at (h+1, a+k) since f(h+1) = a/1 + k = a + k.
 */
export const RATIONAL_META: FunctionMeta<RationalParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'tangent',
    'secant',
    'derivative',
    'asymptotes',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'domain',
    'range',
  ],
  handleConfigs: {
    translate: {
      xParam: 'h',
      yParam: 'k',
      // Handle at (h+1, a+k) since f(h+1) = a/(1) + k = a+k
      toPosition: (p) => [p['h'] + 1, p['a'] + p['k']],
      // When dragged to (x, y): h = x - 1, k = y - a
      fromPosition: (x, y, p) => ({ h: x - 1, k: y - p['a'] }),
    },
    'translate-x': {
      xParam: 'h',
      yParam: 'k',
      toPosition: (p) => [p['h'] + 1, p['a'] + p['k']],
      fromPosition: (x, _y, p) => ({ h: x - 1, k: p['k'] }),
    },
    'translate-y': {
      xParam: 'h',
      yParam: 'k',
      toPosition: (p) => [p['h'] + 1, p['a'] + p['k']],
      fromPosition: (_x, y, p) => ({ h: p['h'], k: y - p['a'] }),
    },
  },
  defaultParams: {
    a: 1,
    h: 0,
    k: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    h: [-10, 10],
    k: [-10, 10],
  },
};
